import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const themes = {
  light: {
    name: 'light',
    colors: {
      // Primary colors
      primary: '#667eea',
      primaryDark: '#764ba2',
      primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      
      // Background colors
      background: '#f8f9fa',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#e9ecef',
      
      // Surface colors (cards, modals)
      surface: '#ffffff',
      surfaceHover: '#f8f9fa',
      
      // Text colors
      text: '#1a1a2e',
      textSecondary: '#6b7280',
      textMuted: '#9ca3af',
      textInverse: '#ffffff',
      
      // Border colors
      border: '#e5e7eb',
      borderLight: '#f3f4f6',
      
      // Status colors
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      
      // Input colors
      inputBackground: '#f9fafb',
      inputBorder: '#e5e7eb',
      inputFocus: '#667eea',
      
      // Shadow
      shadow: 'rgba(0, 0, 0, 0.1)',
      shadowMedium: 'rgba(0, 0, 0, 0.15)',
      shadowDark: 'rgba(0, 0, 0, 0.25)',
      
      // Navbar
      navbarBg: '#1a1a2e',
      navbarText: '#ffffff',
    },
  },
  dark: {
    name: 'dark',
    colors: {
      // Primary colors
      primary: '#818cf8',
      primaryDark: '#a78bfa',
      primaryGradient: 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)',
      
      // Background colors
      background: '#0f172a',
      backgroundSecondary: '#1e293b',
      backgroundTertiary: '#334155',
      
      // Surface colors (cards, modals)
      surface: '#1e293b',
      surfaceHover: '#334155',
      
      // Text colors
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      textMuted: '#64748b',
      textInverse: '#0f172a',
      
      // Border colors
      border: '#334155',
      borderLight: '#475569',
      
      // Status colors
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
      info: '#60a5fa',
      
      // Input colors
      inputBackground: '#1e293b',
      inputBorder: '#334155',
      inputFocus: '#818cf8',
      
      // Shadow
      shadow: 'rgba(0, 0, 0, 0.3)',
      shadowMedium: 'rgba(0, 0, 0, 0.4)',
      shadowDark: 'rgba(0, 0, 0, 0.5)',
      
      // Navbar
      navbarBg: '#0f172a',
      navbarText: '#f1f5f9',
    },
  },
};

// Create Theme Context
const ThemeContext = createContext(undefined);

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or system preference
  const getInitialTheme = () => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('sportsbid-theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    
    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    
    return 'light';
  };

  const [themeName, setThemeName] = useState(getInitialTheme);
  const theme = themes[themeName];

  // Toggle between light and dark
  const toggleTheme = () => {
    setThemeName(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Set specific theme
  const setTheme = (name) => {
    if (name === 'light' || name === 'dark') {
      setThemeName(name);
    }
  };

  // Update localStorage and document when theme changes
  useEffect(() => {
    localStorage.setItem('sportsbid-theme', themeName);
    
    // Update document class for global CSS
    document.documentElement.setAttribute('data-theme', themeName);
    document.body.className = `theme-${themeName}`;
    
    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme.colors.navbarBg);
    }
  }, [themeName, theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('sportsbid-theme');
      if (!savedTheme) {
        setThemeName(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value = {
    theme,
    themeName,
    isDark: themeName === 'dark',
    toggleTheme,
    setTheme,
    colors: theme.colors,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper component for theme-aware styling
export const ThemedView = ({ style, darkStyle, children, ...props }) => {
  const { isDark } = useTheme();
  const combinedStyle = isDark ? { ...style, ...darkStyle } : style;
  return <div style={combinedStyle} {...props}>{children}</div>;
};

export default ThemeContext;
